import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '../../../components/ui/button';

const MIN_VISIBLE = 5;

export interface BrushZoomRange {
  startIndex: number;
  endIndex: number;
}

export function useChartBrushZoom(dataLength: number) {
  const [range, setRange] = useState<BrushZoomRange | null>(null);

  useEffect(() => {
    setRange(null);
  }, [dataLength]);

  const fullEnd = Math.max(0, dataLength - 1);
  const startIndex = range?.startIndex ?? 0;
  const endIndex = range?.endIndex ?? fullEnd;
  const visibleCount = Math.max(1, endIndex - startIndex + 1);
  const isZoomed = dataLength > 0 && (startIndex > 0 || endIndex < fullEnd);
  const canZoomIn = dataLength > MIN_VISIBLE && visibleCount > MIN_VISIBLE;
  const canZoomOut = isZoomed;

  const onBrushChange = useCallback(
    (next?: { startIndex?: number; endIndex?: number }) => {
      if (dataLength <= 0) return;
      const s = next?.startIndex;
      const e = next?.endIndex;
      if (s == null || e == null || !Number.isFinite(s) || !Number.isFinite(e)) return;
      const start = Math.max(0, Math.min(fullEnd, Math.floor(s)));
      const end = Math.max(start, Math.min(fullEnd, Math.floor(e)));
      if (start === 0 && end === fullEnd) {
        setRange(null);
        return;
      }
      setRange({ startIndex: start, endIndex: end });
    },
    [dataLength, fullEnd]
  );

  const zoomIn = useCallback(() => {
    if (!canZoomIn) return;
    const newSpan = Math.max(MIN_VISIBLE, Math.floor(visibleCount * 0.65));
    const mid = Math.floor((startIndex + endIndex) / 2);
    let s = Math.max(0, mid - Math.floor(newSpan / 2));
    let e = Math.min(fullEnd, s + newSpan - 1);
    s = Math.max(0, e - newSpan + 1);
    setRange({ startIndex: s, endIndex: e });
  }, [canZoomIn, visibleCount, startIndex, endIndex, fullEnd]);

  const zoomOut = useCallback(() => {
    if (!canZoomOut) return;
    const newSpan = Math.min(dataLength, Math.ceil(visibleCount / 0.65));
    if (newSpan >= dataLength) {
      setRange(null);
      return;
    }
    const mid = Math.floor((startIndex + endIndex) / 2);
    let s = Math.max(0, mid - Math.floor(newSpan / 2));
    let e = Math.min(fullEnd, s + newSpan - 1);
    s = Math.max(0, e - newSpan + 1);
    setRange({ startIndex: s, endIndex: e });
  }, [canZoomOut, dataLength, visibleCount, startIndex, endIndex, fullEnd]);

  const resetZoom = useCallback(() => {
    setRange(null);
  }, []);

  return useMemo(
    () => ({
      startIndex,
      endIndex,
      isZoomed,
      canZoomIn,
      canZoomOut,
      onBrushChange,
      zoomIn,
      zoomOut,
      resetZoom,
      visibleCount,
    }),
    [
      startIndex,
      endIndex,
      isZoomed,
      canZoomIn,
      canZoomOut,
      onBrushChange,
      zoomIn,
      zoomOut,
      resetZoom,
      visibleCount,
    ]
  );
}

export function ChartBrushZoomControls({
  canZoomIn,
  canZoomOut,
  isZoomed,
  onZoomIn,
  onZoomOut,
  onReset,
  visibleCount,
  totalCount,
}: {
  canZoomIn: boolean;
  canZoomOut: boolean;
  isZoomed: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  visibleCount: number;
  totalCount: number;
}) {
  if (totalCount <= MIN_VISIBLE) return null;

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <span className="text-[11px] text-heritage-grey tabular-nums">
        {isZoomed ? `Showing ${visibleCount} of ${totalCount}` : 'Drag the bar below to zoom'}
      </span>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label="Zoom in"
          title="Zoom in"
          disabled={!canZoomIn}
          onClick={onZoomIn}
        >
          <ZoomIn className="h-4 w-4 text-blueprint-blue" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label="Zoom out"
          title="Zoom out"
          disabled={!canZoomOut}
          onClick={onZoomOut}
        >
          <ZoomOut className="h-4 w-4 text-blueprint-blue" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label="Reset zoom"
          title="Reset zoom"
          disabled={!isZoomed}
          onClick={onReset}
        >
          <RotateCcw className="h-4 w-4 text-blueprint-blue" />
        </Button>
      </div>
    </div>
  );
}
